export interface PaddleCheckoutSession {
  provider: 'paddle';
  transaction_id: string;
  client_token: string;
  environment: 'sandbox' | 'production';
}

interface PaddleEvent {
  name: string;
  data?: { transaction_id?: string };
}

interface PaddleGlobal {
  Environment: { set: (environment: 'sandbox') => void };
  Initialize: (options: { token: string; eventCallback: (event: PaddleEvent) => void }) => void;
  Checkout: { open: (options: { transactionId: string; settings: Record<string, unknown> }) => void };
}

declare global {
  interface Window { Paddle?: PaddleGlobal }
}

const PADDLE_SCRIPT = 'https://cdn.paddle.com/paddle/v2/paddle.js';
let scriptPromise: Promise<PaddleGlobal> | null = null;
let initializedToken = '';
const completions = new Map<string, () => void>();
const closures = new Map<string, () => void>();

function loadPaddle(): Promise<PaddleGlobal> {
  if (window.Paddle) return Promise.resolve(window.Paddle);
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${PADDLE_SCRIPT}"]`);
    const script = existing ?? document.createElement('script');
    const onLoad = (): void => {
      if (window.Paddle) resolve(window.Paddle);
      else reject(new Error('Paddle.js loaded without exposing the checkout API.'));
    };
    script.addEventListener('load', onLoad, { once: true });
    script.addEventListener('error', () => reject(new Error('Unable to load secure Paddle checkout.')), { once: true });
    if (!existing) {
      script.src = PADDLE_SCRIPT;
      script.async = true;
      document.head.appendChild(script);
    }
  });
  return scriptPromise;
}

export async function openPaddleCheckout(session: PaddleCheckoutSession): Promise<'completed' | 'closed'> {
  if (session.provider !== 'paddle' || !session.transaction_id || !session.client_token) {
    throw new Error('The server returned an invalid Paddle checkout session.');
  }
  const paddle = await loadPaddle();
  if (!initializedToken) {
    if (session.environment === 'sandbox') paddle.Environment.set('sandbox');
    paddle.Initialize({
      token: session.client_token,
      eventCallback: (event) => {
        const transactionID = event.data?.transaction_id;
        if (!transactionID) return;
        if (event.name === 'checkout.completed') completions.get(transactionID)?.();
        if (event.name === 'checkout.closed') closures.get(transactionID)?.();
      },
    });
    initializedToken = session.client_token;
  } else if (initializedToken !== session.client_token) {
    throw new Error('Paddle checkout configuration changed. Refresh the page and try again.');
  }

  return new Promise((resolve, reject) => {
    const finish = (result: 'completed' | 'closed'): void => {
      completions.delete(session.transaction_id);
      closures.delete(session.transaction_id);
      resolve(result);
    };
    completions.set(session.transaction_id, () => finish('completed'));
    closures.set(session.transaction_id, () => finish('closed'));
    try {
      paddle.Checkout.open({
        transactionId: session.transaction_id,
        settings: { displayMode: 'overlay', theme: 'light', variant: 'one-page' },
      });
    } catch (error) {
      completions.delete(session.transaction_id);
      closures.delete(session.transaction_id);
      reject(error instanceof Error ? error : new Error('Unable to open Paddle checkout.'));
    }
  });
}
